import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, Placeholder } from "react-bootstrap";

import { useAuth } from "../../contexts/AuthContext";
import Utils from "../../configs/Utils";
import Enums from "../../configs/Enums";
import "../../assets/css/account.scss";

const AccountActivatePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { currentUser, setCurrentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(!!token);
  const [activateStatus, setActivateStatus] = useState("idle");
  const [activateError, setActivateError] = useState("");

  const isActivated = currentUser?.status === "activated" || activateStatus === "success";

  useEffect(() => {
    Utils.setPageTitle("Activate account");

    const activateAccount = async () => {
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const data = await Utils._apiRequest({
          path: Enums.endpoints.auth.ACTIVATE,
          pathParams: { token },
          type: "GET",
        });

        if (data?.errors) {
          const errorMessage =
            data.errors.message ||
            data.errors.token ||
            "Activation link is invalid or has expired.";
          throw new Error(errorMessage);
        }

        const userPayload = data?.user || data;
        if (!userPayload?._id) {
          throw new Error(data?.message || "Unable to activate this account.");
        }

        setCurrentUser(userPayload);
        setActivateStatus("success");
      } catch (error) {
        setActivateStatus("error");
        setActivateError(error.message || "Activation failed. Please request a new link.");
      } finally {
        setIsLoading(false);
      }
    };

    activateAccount();
  }, [token, setCurrentUser]);

  return (
    <main className="page-main account-page container">
      <section className="activate-wrapper">
      {isLoading ? (
        <div className="auth-message-card">
          <Placeholder animation="glow" as="h2" className="border-bottom pb-2 mb-3">
            <Placeholder xs={9} />
          </Placeholder>
          <Placeholder animation="glow" as="p">
            <Placeholder xs={12} />{" "}
            <Placeholder xs={4} />
          </Placeholder>
          <Placeholder animation="glow" as="p">
            <Placeholder xs={8} />
          </Placeholder>
          <Placeholder animation="glow" as="p">
            <Placeholder.Button variant="primary" xs={3} />
          </Placeholder>
        </div>
      ) : activateStatus === "error" ? (
        <div className="auth-message-card">
          <h2 className="border-bottom pb-2 mb-3">Activation failed</h2>
          <p>{activateError}</p>
          <p>The activation link may be expired or already used.</p>
          <div className="d-flex gap-2 flex-wrap mt-3">
            <Button variant="primary" onClick={() => navigate("/login")}>Go to login</Button>
            <Button variant="outline-secondary" onClick={() => navigate("/account/activate")}>Back to activation notice</Button>
          </div>
        </div>
      ) : isActivated ? (
        <div className="auth-message-card">
          <h2 className="border-bottom pb-2 mb-3">Your account has been successfully activated</h2>
          <p>Your email is verified and your account is ready to use.</p>
          <p>You can continue to dashboard now.</p>
          <Button onClick={() => navigate("/")} variant="dark">Go to dashboard</Button>
        </div>
      ) : (
        <div className="auth-message-card">
          <h2 className="border-bottom pb-2 mb-3">Activate your account</h2>
          <p>Thank you for registering.</p>
          <p>
            Please check your email and click the activation link to activate your account.
          </p>
          {currentUser?.email ? (
            <p className="mb-3">
              Activation email was sent to <strong>{currentUser.email}</strong>.
            </p>
          ) : null}
          <h5 className="mt-4">Didn&lsquo;t receive the email?</h5>
          <ul>
            <li>Check spam or junk folder.</li>
            <li>Make sure you entered the correct email address when registering.</li>
          </ul>
          <p className="mb-3">After activation, you can login and access the dashboard normally.</p>
          <div className="d-flex gap-2 flex-wrap mt-3">
            <Button variant="dark" onClick={() => navigate("/login")}>Go to login</Button>
            <Button variant="outline-secondary" as={Link} to="/register">Register another account</Button>
          </div>
        </div>
      )}
      </section>
    </main>
  );
};

export default AccountActivatePage;